import * as React from 'react';
import {
  Tabs as ShadcnTabs,
  TabsList as ShadcnTabsList,
  TabsTrigger as ShadcnTabsTrigger,
  TabsContent as ShadcnTabsContent,
} from '@/hen-ui/shadcn/tabs';
export interface TabsProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof ShadcnTabs>,
    'children' | 'defaultValue' | 'orientation'
  > {
  tabs: {
    value: string;
    label: React.ReactNode;
    disabled?: boolean;
    content?: React.ReactNode;
  }[];
  orientation?: 'horizontal' | 'vertical';
  defaultValue?: string;
}

/**
 * Tabs component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui tabs (Radix UI Tabs primitive) with HEN-specific
 * variants and design tokens. All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Basic usage - horizontal orientation by default
  <Tabs
    tabs={[
      {
        value: 'tab1',
        label: 'Tab 1',
        content: <div>Content for Tab 1</div>
      },
      {
        value: 'tab2',
        label: 'Tab 2',
        content: <div>Content for Tab 2</div>
      },
      {
        value: 'tab3',
        label: 'Tab 3',
        content: <div>Content for Tab 3</div>
      }
    ]}
  />

 *
 * @example
 * //  Vertical Orientation
  <Tabs
    orientation='vertical'
    tabs={[
      {
        label: 'Option 1',
        value: 'option1',
        content: <div>Content for Option 1</div>
      },
      {
        label: 'Option 2',
        value: 'option2',
        content: <div>Content for Option 2</div>
      },
      {
        label: 'Option 3',
        value: 'option3',
        content: <div>Content for Option 3</div>
      }
    ]}
  />
 *
 * @example
 * // Disabled Tab Trigger
  <Tabs
    tabs={[
      {
        content: <div className="text-sm text-neutral-900">This tab is available.</div>,
        label: 'Available',
        value: 'available'
      },
      {
        content: <div className="text-sm text-neutral-900">This content is not accessible.</div>,
        disabled: true,
        label: 'Disabled',
        value: 'disabled'
      },
      {
        content: <div className="text-sm text-neutral-900">Another available tab content.</div>,
        label: 'Another Tab',
        value: 'another'
      }
    ]}
  />
 */

export const Tabs = React.forwardRef<
  React.ElementRef<typeof ShadcnTabs>,
  TabsProps
>(({ className, tabs, defaultValue, orientation, ...props }, ref) => {
  return (
    <ShadcnTabs
      ref={ref}
      className={className}
      defaultValue={defaultValue ? defaultValue : undefined}
      orientation={orientation ? orientation : 'horizontal'}
      {...props}
    >
      <ShadcnTabsList>
        {tabs.map((tab) => (
          <ShadcnTabsTrigger
            key={tab.value}
            value={tab.value}
            disabled={tab.disabled}
          >
            {tab.label}
          </ShadcnTabsTrigger>
        ))}
      </ShadcnTabsList>
      {tabs.map((tab) => (
        <ShadcnTabsContent key={tab.value} value={tab.value}>
          {tab.content}
        </ShadcnTabsContent>
      ))}
    </ShadcnTabs>
  );
});

Tabs.displayName = 'Tabs';
